import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, HelpCircle, Plus } from 'lucide-react';
import { InboxIcon } from '@heroicons/react/outline';
import { Button } from 'components/Button';
import { WayflyerCapitalBanner } from 'components/WayflyerCapitalBanner';
import { ProductUpdates } from 'components/ProductUpdates';
import { useStore } from 'context/store-context';
import { useUser } from 'context/user-context';
import { MERCHANT_REQUESTS } from 'gql/queries';
import {
  canManageRequests,
  MerchantRequestsQueryResult,
} from 'types/resolution';
import { isShipaidAdmin } from 'utils';
import { userRoles } from 'types/user';
import { getMerchantRequestsContext } from 'utils/merchantRequestsTransport';
import { withRequestReturnTo } from 'utils/requestNavigation';
import { ProtectionSummary } from './ProtectionReport';
import { getDefaultReportDates } from './requestReportDateRange';
import { HomeSetup } from './HomeSetup';
import { HomeActivity } from './HomeActivity';

const validCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

const resourceLinkClass =
  'text-sm text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500';

function ResourceLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={resourceLinkClass}
    >
      {children}
    </a>
  );
}

function HomeResources({ allowed }: { allowed: boolean }) {
  const [open, setOpen] = useState(true);
  return (
    <footer className="border-t border-gray-200 pt-5">
      <div className="flex items-center">
        <button
          type="button"
          aria-expanded={open}
          aria-controls="home-resources"
          onClick={() => setOpen(!open)}
          className="inline-flex items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <HelpCircle className="h-4 w-4" aria-hidden="true" />
          Help &amp; resources
          <ChevronDown
            className={`h-4 w-4 ${open ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
      </div>
      {open && (
        <div
          id="home-resources"
          className="mt-5 grid gap-6 rounded-xl border border-gray-200 bg-white p-5 md:grid-cols-3"
        >
          <section aria-label="Guides and support" className="space-y-3">
            <h2 className="text-sm font-semibold text-gray-900">
              Guides &amp; support
            </h2>
            <ul className="space-y-2">
              {allowed && (
                <>
                  <li>
                    <Link
                      to="/settings/protection#installation"
                      className={resourceLinkClass}
                    >
                      Installation &amp; Checkout+ setup
                    </Link>
                    <p className="text-xs text-gray-500">
                      Choose a setup, customize and preview Checkout+, or
                      request installation help.
                    </p>
                  </li>
                  <li>
                    <Link
                      to="/settings/resolution-portal/returns/policy"
                      className={resourceLinkClass}
                    >
                      Returns &amp; exchanges setup
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/requests/reports?report=requests"
                      className={resourceLinkClass}
                    >
                      Returns &amp; exchanges reports
                    </Link>
                  </li>
                </>
              )}
              <li>
                <ResourceLink href="https://help.shipaid.com/en/">
                  Help center
                </ResourceLink>
              </li>
              <li>
                <ResourceLink href="https://help.shipaid.com/en/category/shipping-guarantee-promise-f5d2mo/">
                  Delivery Guarantee guides
                </ResourceLink>
              </li>
              <li>
                <ResourceLink href="https://guides.shipaid.com/showcase/cmguwoy8v00a71y0irohihcz7?utm_source=link">
                  Interactive demos
                </ResourceLink>
              </li>
              <li>
                <ResourceLink href="https://calendly.com/shipaid-support/expert-call">
                  Book onboarding
                </ResourceLink>
              </li>
              <li>
                <ResourceLink href="https://help.shipaid.com/en/article/shipaid-v2-impact-scaling-shipping-guarantee-and-purpose-vmoz0d/">
                  Impact setup guide
                </ResourceLink>
              </li>
            </ul>
          </section>
          <section aria-label="Optional features" className="space-y-3">
            <h2 className="text-sm font-semibold text-gray-900">
              Optional features
            </h2>
            {!allowed && (
              <p className="text-xs text-gray-500">
                Ask your store admin to enable optional features.
              </p>
            )}
            <ul className="space-y-2">
              {allowed && (
                <>
                  <li>
                    <Link
                      to="/settings/resolution-portal/returns/exchanges"
                      className={resourceLinkClass}
                    >
                      Exchange options
                    </Link>
                    <p className="text-xs text-gray-500">
                      Enable variant and different-product exchanges.
                    </p>
                  </li>
                  <li>
                    <Link
                      to="/settings/resolution-portal/returns/incentives"
                      className={resourceLinkClass}
                    >
                      Return &amp; exchange incentives
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/settings/resolution-portal/returns/approval"
                      className={resourceLinkClass}
                    >
                      Return &amp; exchange approvals
                    </Link>
                    <p className="text-xs text-gray-500">
                      Choose manual review or automatic approval.
                    </p>
                  </li>
                  <li>
                    <Link
                      to="/settings/claim-automations"
                      className={resourceLinkClass}
                    >
                      Delivery Guarantee automations
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/settings/integration"
                      className={resourceLinkClass}
                    >
                      Klaviyo &amp; other integrations
                    </Link>
                  </li>
                  <li>
                    <Link to="/settings/impact" className={resourceLinkClass}>
                      Impact settings
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/order-settings/all"
                      className={resourceLinkClass}
                    >
                      Order editing settings
                    </Link>
                  </li>
                </>
              )}
              <li>
                <ResourceLink href="https://guides.shipaid.com/demo/cmg86bg8d1lia2nom8c39gu8s?utm_source=link">
                  Add to Cart+ guide
                </ResourceLink>
              </li>
              <li>
                <ResourceLink href="https://guides.shipaid.com/demo/cmcjqito302bq030i91qp6eif">
                  Fraud Guard guide
                </ResourceLink>
              </li>
            </ul>
          </section>
          <ProductUpdates />
        </div>
      )}
    </footer>
  );
}

export const Home = () => {
  const { storeId, storeName, storeProperties, userStores, updateUserStore } =
    useStore();
  const { role, user } = useUser();
  const allowed = canManageRequests(role, isShipaidAdmin(user));
  const reportDates = getDefaultReportDates();
  const { data, loading, error, refetch } =
    useQuery<MerchantRequestsQueryResult>(MERCHANT_REQUESTS, {
      context: getMerchantRequestsContext(),
      variables: {
        input: {
          storeId,
          type: 'ALL',
          group: 'ALL',
          orderBy: 'UPDATED_AT_DESC',
          limit: 5,
          offset: 0,
        },
      },
      skip: !storeId || !allowed,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    });
  const response = data?.merchantRequests;
  const counts =
    response && Array.isArray(response.counts) ? response.counts : [];
  const needsAction = counts.find(
    ({ group }) => group === 'NEEDS_ACTION',
  )?.count;
  const waiting = counts.find(({ group }) => group === 'WAITING')?.count;
  const completeResponse =
    response?.storeId === storeId &&
    validCount(needsAction) &&
    validCount(waiting) &&
    Array.isArray(response?.nodes);
  // Retained Apollo responses must never display work from a previous store.
  const requestsReady = Boolean(
    allowed && !loading && !error && completeResponse,
  );
  const recentRequests = requestsReady ? response!.nodes.slice(0, 5) : [];
  const reportHref = allowed
    ? `/requests/reports?report=protection&startDate=${reportDates.startDate}&endDate=${reportDates.endDate}`
    : undefined;
  const currentStoreName =
    storeName ||
    (storeProperties?.id === storeId ? storeProperties?.name : undefined) ||
    'Your store';
  const displayName = user?.displayName?.trim();

  return (
    <main className="mx-auto max-w-7xl space-y-6 px-4 py-6 pb-12 sm:px-6 md:px-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
            {displayName ? `Welcome, ${displayName}` : 'Welcome to ShipAid'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Your store at a glance, and what needs you next.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
            {userStores && userStores.length > 1 ? (
              <label className="max-w-full">
                <span className="sr-only">Store</span>
                <select
                  value={storeId || ''}
                  disabled={!updateUserStore}
                  onChange={(event) => {
                    const store = userStores.find(
                      ({ storeId: id }) => id === event.target.value,
                    );
                    if (store) updateUserStore?.(store);
                  }}
                  className="max-w-full rounded-md border-gray-300 bg-white py-1.5 pl-3 pr-8 text-sm text-gray-700 focus:border-blue-500 focus:ring-blue-500"
                >
                  {!userStores.some((store) => store.storeId === storeId) && (
                    <option value={storeId || ''}>Choose a store</option>
                  )}
                  {userStores.map((store) => (
                    <option key={store.storeId} value={store.storeId}>
                      {store.name || store.domain}
                    </option>
                  ))}
                </select>
              </label>
            ) : storeId ? (
              <span className="font-medium text-gray-600">
                {currentStoreName}
              </span>
            ) : null}
            {allowed && (
              <Link
                className="text-xs text-blue-700 hover:underline"
                to="/settings/integration"
              >
                Manage stores
              </Link>
            )}
          </div>
        </div>
        {storeId && allowed && (
          <Link
            to={withRequestReturnTo('/requests/new-issue', '/')}
            className="inline-flex items-center gap-2 rounded-md bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            New request
          </Link>
        )}
      </header>
      {!storeId ? (
        <p className="rounded-xl border border-gray-200 bg-white p-5 text-sm text-gray-500">
          Choose a store to view your home.
        </p>
      ) : (
        <>
          <HomeSetup key={storeId} />
          {allowed && (
            <section
              aria-labelledby="home-attention-title"
              className="rounded-xl border border-blue-100 bg-blue-50 p-4 sm:p-5"
            >
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="rounded-lg bg-white p-2 text-blue-700">
                    <InboxIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <h2
                      id="home-attention-title"
                      className="text-sm font-semibold text-gray-900"
                    >
                      Needs attention
                    </h2>
                    <p className="mt-1 text-xs text-gray-600">
                      Current requests, across all dates.
                    </p>
                  </div>
                </div>
                {loading ? (
                  <p role="status" className="text-sm text-gray-600">
                    Loading current requests…
                  </p>
                ) : !requestsReady ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <p role="alert" className="text-sm text-red-700">
                      Current requests could not be loaded.
                    </p>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        void refetch().catch(() => undefined);
                      }}
                    >
                      Retry requests
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                    <Link
                      to="/requests?group=NEEDS_ACTION"
                      aria-label={`Needs action: ${needsAction}`}
                      className="group flex items-center gap-3 rounded-md p-1 text-blue-900 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <div>
                        <span className="text-2xl font-semibold tabular-nums">
                          {needsAction}
                        </span>
                        <span className="ml-2 text-sm font-medium">
                          Needs action
                        </span>
                        {needsAction === 0 && (
                          <p className="text-xs">You’re all caught up.</p>
                        )}
                      </div>
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                    <Link
                      to="/requests?group=WAITING"
                      aria-label={`Waiting: ${waiting}`}
                      className="rounded-md p-1 text-sm text-gray-600 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <span className="font-semibold tabular-nums text-gray-900">
                        {waiting}
                      </span>{' '}
                      Waiting
                    </Link>
                  </div>
                )}
              </div>
            </section>
          )}
          <WayflyerCapitalBanner />
          <section aria-label="Delivery Guarantee snapshot">
            <p className="mb-2 text-xs text-gray-500">Last 30 days · UTC</p>
            <ProtectionSummary
              startDate={`${reportDates.startDate}T00:00:00.000Z`}
              endDate={`${reportDates.endDate}T23:59:59.999Z`}
              reportHref={reportHref}
            />
          </section>
          {(allowed || role === userRoles.VIEWER) && (
            <HomeActivity
              key={`${storeId}:${role || ''}`}
              requests={recentRequests}
              loading={loading}
              ready={requestsReady}
            />
          )}
        </>
      )}
      <HomeResources allowed={allowed} />
    </main>
  );
};
